import React, { useMemo } from 'react';

const W = 680;
const H = 320;
const M = { l: 50, r: 84, t: 20, b: 34 };
const IW = W - M.l - M.r;
const IH = H - M.t - M.b;

const ymToT = (ym) => {
  const [y, m] = String(ym).split('-').map(Number);
  return y + (m - 0.5) / 12;
};

function niceLogTicks(lo, hi) {
  const ticks = [];
  for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e += 1) {
    for (const m of [1, 2, 5]) {
      const v = m * 10 ** e;
      if (v >= lo && v <= hi) ticks.push(v);
    }
  }
  return ticks;
}

const fmtTick = (v) => (v >= 1000 ? `${v / 1000}k` : String(v));

export const CASE_STYLE = {
  seller: { stroke: '#6E8495', dash: '8 6', width: 3, label: 'Seller' },
  base: { stroke: '#C77700', dash: null, width: 3, label: 'Our base' },
  custom: { stroke: '#163A4F', dash: '2 5', width: 3, label: 'Your case' },
};

/**
 * Semi-log rate–time chart: actual history (annual dots + recent monthly dots),
 * the seller and our base forecasts as lines, conservative↔aggressive as a band.
 */
export default function ForecastChart({ forecast, show = 'all', horizonYears = 20 }) {
  const model = useMemo(() => {
    if (!forecast) return null;
    const { history, cases, startDate } = forecast;
    const t0 = Math.min(...history.annual.map((r) => r.year), ymToT(startDate) - 10);
    const tStart = ymToT(startDate);
    const t1 = tStart + horizonYears;
    const allQ = [
      ...history.annual.map((r) => r.mcfpd),
      ...history.recent.map((r) => r.mcfpd),
      ...Object.values(cases).flatMap((c) =>
        c.series.filter((p) => ymToT(p.month) <= t1).map((p) => p.mcfpd),
      ),
    ].filter((q) => q > 0);
    const qMax = Math.max(...allQ) * 1.15;
    const qMin = Math.max(100, Math.min(...allQ) * 0.85);
    const yLo = qMin;
    const yHi = qMax;
    const x = (t) => M.l + ((t - t0) / (t1 - t0)) * IW;
    const y = (q) =>
      M.t + IH - ((Math.log10(q) - Math.log10(yLo)) / (Math.log10(yHi) - Math.log10(yLo))) * IH;
    const pathOf = (series) =>
      series
        .filter((p) => ymToT(p.month) <= t1 && p.mcfpd > 0)
        .map((p, i) => `${i ? 'L' : 'M'}${x(ymToT(p.month)).toFixed(1)},${y(p.mcfpd).toFixed(1)}`)
        .join(' ');
    let band = null;
    if (cases.conservative && cases.aggressive) {
      const top = cases.aggressive.series.filter((p) => ymToT(p.month) <= t1);
      const bot = cases.conservative.series.filter((p) => ymToT(p.month) <= t1);
      const up = top.map((p) => `${x(ymToT(p.month)).toFixed(1)},${y(p.mcfpd).toFixed(1)}`);
      const dn = bot
        .slice()
        .reverse()
        .map((p) => `${x(ymToT(p.month)).toFixed(1)},${y(Math.max(yLo, p.mcfpd)).toFixed(1)}`);
      band = `M${up.join(' L')} L${dn.join(' L')} Z`;
    }
    const yearTicks = [];
    for (let yr = Math.ceil(t0 / 5) * 5; yr <= t1; yr += 5) yearTicks.push(yr);
    const endLabel = (c) => {
      const pts = c.series.filter((p) => ymToT(p.month) <= t1);
      const last = pts[pts.length - 1];
      return last ? { x: x(ymToT(last.month)) + 6, y: y(last.mcfpd) } : null;
    };
    return {
      x,
      y,
      yLo,
      yHi,
      t0,
      t1,
      tStart,
      band,
      yTicks: niceLogTicks(yLo, yHi),
      yearTicks,
      pathOf,
      endLabel,
    };
  }, [forecast, horizonYears]);

  if (!forecast || !model) return null;
  const { cases, history } = forecast;
  const { x, y, band, yTicks, yearTicks, pathOf, endLabel, tStart } = model;
  const lineIds = show === 'seller-vs-ours' ? ['seller', 'base'] : ['seller', 'base', 'custom'];
  const labels = lineIds
    .filter((id) => cases[id])
    .map((id) => ({ id, ...endLabel(cases[id]) }))
    .sort((a, b) => a.y - b.y);
  // de-overlap end labels
  for (let i = 1; i < labels.length; i += 1) {
    if (labels[i].y - labels[i - 1].y < 17) labels[i].y = labels[i - 1].y + 17;
  }

  return (
    <svg
      className="mp-chart"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${forecast.wellName} gas rate on a log scale: actual history and forecast cases.`}
    >
      <g fontSize="13" fill="#5B6B78">
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={M.l} x2={M.l + IW} y1={y(v)} y2={y(v)} stroke="#ECEAE3" />
            <text x={M.l - 8} y={y(v) + 4} textAnchor="end">
              {fmtTick(v)}
            </text>
          </g>
        ))}
        {yearTicks.map((yr) => (
          <text key={yr} x={x(yr)} y={H - 10} textAnchor="middle">
            {yr}
          </text>
        ))}
        <text x={M.l - 8} y={M.t - 4} textAnchor="end" className="lbl" fontSize="12">
          Mcf/d
        </text>
      </g>
      {show !== 'seller-vs-ours' && band ? (
        <path d={band} fill="#FDF3E1" stroke="#F0D9AE" />
      ) : null}
      {lineIds
        .filter((id) => cases[id])
        .map((id) => (
          <path
            key={id}
            d={pathOf(cases[id].series)}
            fill="none"
            stroke={CASE_STYLE[id].stroke}
            strokeWidth={CASE_STYLE[id].width}
            strokeDasharray={CASE_STYLE[id].dash || undefined}
            strokeLinejoin="round"
          />
        ))}
      <polyline
        points={history.annual.map((r) => `${x(r.year + 0.5)},${y(r.mcfpd)}`).join(' ')}
        fill="none"
        stroke="#0C1F2E"
        strokeOpacity="0.35"
        strokeWidth="1.5"
      />
      {history.annual.map((r) => (
        <circle key={r.year} cx={x(r.year + 0.5)} cy={y(r.mcfpd)} r="4" fill="#0C1F2E">
          <title>{`${r.year}: ${Math.round(r.mcfpd).toLocaleString()} Mcf/d (annual avg)`}</title>
        </circle>
      ))}
      {history.recent.map((r) => (
        <circle key={r.month} cx={x(ymToT(r.month))} cy={y(r.mcfpd)} r="2.6" fill="#0C1F2E" fillOpacity="0.55">
          <title>{`${r.month}: ${Math.round(r.mcfpd).toLocaleString()} Mcf/d`}</title>
        </circle>
      ))}
      <line x1={x(tStart)} x2={x(tStart)} y1={M.t} y2={M.t + IH} stroke="#0C1F2E" strokeDasharray="2 4" />
      <text x={x(tStart) + 6} y={M.t + 12} fontSize="13" fill="#0C1F2E" className="lbl">
        forecast starts
      </text>
      {labels.map((l) => (
        <text
          key={l.id}
          x={l.x}
          y={l.y + 4}
          fontSize="13"
          fontWeight="700"
          fill={CASE_STYLE[l.id].stroke}
          className="lbl"
        >
          {CASE_STYLE[l.id].label}
        </text>
      ))}
    </svg>
  );
}
